import { find, getOr, get, includes } from 'lodash/fp';
import {
  CLIENT_TYPE,
  OnlineStep,
  ONLINE_CLIENT_TYPE,
  WebStep,
  WebPage,
} from './constants';

type Dealer = any;

export const InStoreControlPanelSteps = {
  DRIVER_LICENSE: {
    id: 'driverLicense',
    name: "Driver's License",
  },
  DOCUMENT_SCAN: {
    id: 'documentScan',
    name: 'Document Scan',
  },
  VEHICLE_SELECTION: {
    id: 'vehicleSelection',
    name: 'Vehicle Selection',
    required: true,
  },
  TEST_DRIVE: {
    id: 'testDrive',
    name: 'Test Drive',
  },
  TRADE_IN: {
    id: 'tradeIn',
    name: 'Trade-In',
  },
  CUSTOMER_INFO: {
    id: 'customerInfo',
    name: 'Customer Info',
    required: true,
  },
  CREDIT_APPLICATION: {
    id: 'creditApplication',
    name: 'Credit Application',
  },
  FORM_LIBRARY: {
    id: 'formLibrary',
    name: 'Forms Library',
  },
  CAR_PAYMENT_BUILDER: {
    id: 'carPaymentBuilder',
    name: 'Ownership Options',
  },
  PROTECTION_PLANS: {
    id: 'protectionPlans',
    name: 'Protection Plans',
  },
  PRICING_REVIEW: {
    id: 'pricingReview',
    name: 'Pricing Review',
  },
  ACCESSORIES: {
    id: 'accessories',
    name: 'Accessories',
  },
};

export const OnlineControlPanelSteps = {
  [OnlineStep.browse]: {
    id: OnlineStep.browse,
    name: 'Browse Vehicles',
    required: true,
    locked: true,
  },
  [OnlineStep.customerInfo]: {
    id: OnlineStep.customerInfo,
    name: 'Customer Info',
    required: true,
  },
  [OnlineStep.tradeIn]: {
    id: OnlineStep.tradeIn,
    name: 'Value Your Trade',
  },
  [OnlineStep.paymentBuilder]: {
    id: OnlineStep.paymentBuilder,
    name: 'Customize Your Payments',
  },
  [OnlineStep.testDrive]: {
    id: OnlineStep.testDrive,
    name: 'Test Drive',
  },
  [OnlineStep.creditApplication]: {
    id: OnlineStep.creditApplication,
    name: 'Credit Application',
  },
  [OnlineStep.upgradePackages]: {
    id: OnlineStep.upgradePackages,
    name: 'Protection Plans',
  },
  [OnlineStep.deposit]: {
    id: OnlineStep.deposit,
    name: 'Secure Deposit',
  },
  [OnlineStep.schedulePickup]: {
    id: OnlineStep.schedulePickup,
    name: 'Finish In Store',
    locked: true,
  },
  [OnlineStep.accessories]: {
    id: OnlineStep.accessories,
    name: 'Accessories',
  },
  [OnlineStep.documentUpload]: {
    id: OnlineStep.documentUpload,
    name: 'Document Upload',
  },
  [OnlineStep.signDocuments]: {
    id: OnlineStep.signDocuments,
    name: 'Sign Documents',
  },
};

export const WebControlPanelSteps = {
  [WebStep.payments]: {
    id: WebStep.payments,
    name: 'Payment Options',
  },
  [WebStep.creditApplication]: {
    id: WebStep.creditApplication,
    name: 'Credit Application',
  },
  [WebStep.tradeIn]: {
    id: WebStep.tradeIn,
    name: 'Trade In',
  },
  [WebStep.appointments]: {
    id: WebStep.appointments,
    name: 'Appointments',
  },
  [WebStep.accessories]: {
    id: WebStep.accessories,
    name: 'Accessories',
  },
  [WebStep.protectionPlans]: {
    id: WebStep.protectionPlans,
    name: 'Protection Plans',
  },
};

export const OnlinePages = [
  {
    id: 'browse',
    step: OnlineStep.browse,
    path: '/browse/:dealerId/:dealId?',
    websitePath: '/w/browse/:websiteId/:dealId?',
  },
  {
    id: 'userInfo',
    step: OnlineStep.customerInfo,
    path: '/user-info/:dealerId/:dealId?',
    websitePath: '/w/user-info/:websiteId/:dealId?',
  },
  {
    id: 'tradeIn',
    step: OnlineStep.tradeIn,
    path: '/trade-in/:dealerId/:dealId?',
    websitePath: '/w/trade-in/:websiteId/:dealId?',
  },
  {
    id: 'tradeInResult',
    step: OnlineStep.tradeIn,
    path: '/trade-in-result/:dealerId/:dealId?',
    websitePath: '/w/trade-in-result/:websiteId/:dealId?',
  },
  {
    id: 'paymentBuilder',
    step: OnlineStep.paymentBuilder,
    path: '/payment-builder/:dealerId/:dealId?',
    websitePath: '/w/payment-builder/:websiteId/:dealId?',
  },
  {
    id: 'vehicleDetail',
    step: OnlineStep.browse,
    path: '/v/:dealerId/:vehicleId/:dealId?',
    websitePath: '/w/v/:websiteId/:vehicleId/:dealId?',
  },
  {
    id: 'creditPull',
    step: OnlineStep.paymentBuilder,
    path: '/credit-pull/:dealerId/:dealId?',
    websitePath: '/w/credit-pull/:websiteId/:dealId?',
  },
  {
    id: 'extraCreditPull',
    step: OnlineStep.paymentBuilder,
    path: '/extra-credit-pull/:dealerId/:dealId',
    websitePath: '/w/extra-credit-pull/:websiteId/:dealId',
  },
  {
    id: 'creditPullQuestions',
    step: OnlineStep.paymentBuilder,
    path: '/credit-pull-questions/:dealerId/:dealId?',
    websitePath: '/w/credit-pull-questions/:websiteId/:dealId?',
  },
  {
    id: 'creditApplication',
    step: OnlineStep.creditApplication,
    path: '/pre-approval/:dealerId/:dealId?',
    websitePath: '/w/pre-approval/:websiteId/:dealId?',
  },
  {
    id: 'protectionPlans',
    step: OnlineStep.upgradePackages,
    path: '/upgrade-packages/:dealerId/:dealId',
    websitePath: '/w/upgrade-packages/:websiteId/:dealId',
  },
  {
    id: 'schedulePickup',
    step: OnlineStep.schedulePickup,
    path: '/schedule-pickup/:dealerId/:dealId',
    websitePath: '/w/schedule-pickup/:websiteId/:dealId',
  },
  {
    id: 'scheduleTestDrive',
    step: OnlineStep.testDrive,
    path: '/schedule-test-drive/:dealerId/:dealId?',
    websitePath: '/w/schedule-test-drive/:websiteId/:dealId?',
  },
  {
    id: 'deposit',
    step: OnlineStep.deposit,
    path: '/deposit/:dealerId/:dealId',
    websitePath: '/w/deposit/:websiteId/:dealId',
  },
  {
    id: 'summary',
    path: '/summary/:dealerId/:dealId',
    websitePath: '/w/summary/:websiteId/:dealId',
  },
  {
    id: 'accessories',
    step: OnlineStep.accessories,
    path: '/accessories/:dealerId/:dealId',
    websitePath: '/w/accessories/:websiteId/:dealId',
  },
  {
    id: 'insurance',
    path: '/insurance/:dealerId/:dealId',
    websitePath: '/w/insurance/:websiteId/:dealId',
  },
  {
    id: 'documentUpload',
    step: OnlineStep.documentUpload,
    path: '/document-upload/:dealerId/:dealId',
    websitePath: '/w/document-upload/:websiteId/:dealId',
  },
  {
    id: 'signDocuments',
    path: '/sign-documents/:dealerId/:dealId',
    websitePath: '/w/sign-documents/:websiteId/:dealId',
  },
];

export const WebPages = {
  [WebPage.browse]: {
    id: WebPage.browse,
    step: WebPage.browse,
    path: '/browse/:websiteId/:dealId?',
  },
  [WebStep.tradeIn]: {
    id: WebStep.tradeIn,
    step: WebStep.tradeIn,
    path: '/trade/:websiteId/:dealId?',
  },
  [WebStep.payments]: {
    id: WebStep.payments,
    step: WebStep.payments,
    path: '/payments/:websiteId/:dealId?',
  },
  [WebPage.settings]: {
    id: WebPage.settings,
    step: WebPage.settings,
    path: '/settings/:websiteId/:dealId?',
  },
  creditPull: {
    id: 'creditPull',
    step: OnlineStep.paymentBuilder,
    path: '/credit-pull/:dealerId/:dealId',
  },
  extraCreditPull: {
    id: 'extraCreditPull',
    step: OnlineStep.paymentBuilder,
    path: '/extra-credit-pull/:websiteId/:dealId',
  },
  creditPullQuestions: {
    id: 'creditPullQuestions',
    step: OnlineStep.paymentBuilder,
    path: '/credit-pull-questions/:websiteId/:dealId',
  },
  [WebStep.creditApplication]: {
    id: WebStep.creditApplication,
    step: WebStep.creditApplication,
    path: '/application/:websiteId/:dealId?',
  },
  [WebStep.protectionPlans]: {
    id: WebStep.protectionPlans,
    step: WebStep.protectionPlans,
    path: '/protection/:dealerId/:dealId?',
  },
  [WebStep.appointments]: {
    id: WebStep.appointments,
    step: WebStep.appointments,
    path: '/appointments/:websiteId/:dealId?',
  },
  [WebPage.summary]: {
    id: WebPage.summary,
    step: WebPage.summary,
    path: '/summary/:websiteId/:dealId?',
  },
  [WebStep.accessories]: {
    id: WebStep.accessories,
    step: WebStep.accessories,
    path: '/accessories/:websiteId/:dealId?',
  },
  [WebPage.examples]: {
    id: WebPage.examples,
    step: WebPage.examples,
    path: '/examples/:websiteId/:dealId?',
  },
  [WebStep.documentUpload]: {
    id: WebStep.documentUpload,
    step: WebStep.documentUpload,
    path: '/document-upload/:websiteId/:dealId?',
  },
  [WebPage.lending]: {
    id: WebPage.lending,
    step: WebPage.lending,
    path: '/lending/:dealerId/:dealId?',
  },
  [WebPage.offers]: {
    id: WebPage.offers,
    step: WebPage.offers,
    path: '/offers/:dealerId/:dealId?',
  },
  byo: {
    id: 'byo',
    step: 'byo',
    path: '/byo/:websiteId/:dealId?',
  },
  'byo-summary': {
    id: 'byo-summary',
    step: 'byo-summary',
    path: '/byo-summary/:websiteId/:dealId',
  },
  'no-byo': {
    id: 'no-byo',
    step: 'no-byo',
    path: '/no-byo/:websiteId?/:dealId?',
  },
};

export const ControlPanelSteps = {
  [CLIENT_TYPE.IN_STORE]: InStoreControlPanelSteps,
  [CLIENT_TYPE.ONLINE]: OnlineControlPanelSteps,
  [ONLINE_CLIENT_TYPE.WEB]: WebControlPanelSteps,
};

export const getDefaultInstoreFlow = () => [
  InStoreControlPanelSteps.CUSTOMER_INFO,
  InStoreControlPanelSteps.VEHICLE_SELECTION,
  InStoreControlPanelSteps.DRIVER_LICENSE,
  InStoreControlPanelSteps.CAR_PAYMENT_BUILDER,
  InStoreControlPanelSteps.PROTECTION_PLANS,
  InStoreControlPanelSteps.DOCUMENT_SCAN,
  InStoreControlPanelSteps.TRADE_IN,
  InStoreControlPanelSteps.CREDIT_APPLICATION,
  InStoreControlPanelSteps.FORM_LIBRARY,
  InStoreControlPanelSteps.TEST_DRIVE,
];

export const getDefaultOnlineFlow = () => [
  OnlineControlPanelSteps[OnlineStep.browse],
  OnlineControlPanelSteps[OnlineStep.customerInfo],
  OnlineControlPanelSteps[OnlineStep.tradeIn],
  OnlineControlPanelSteps[OnlineStep.paymentBuilder],
  OnlineControlPanelSteps[OnlineStep.testDrive],
  OnlineControlPanelSteps[OnlineStep.creditApplication],
  OnlineControlPanelSteps[OnlineStep.schedulePickup],
];

export const getDefaultWebFlow = () => [
  WebControlPanelSteps[WebStep.payments],
  WebControlPanelSteps[WebStep.creditApplication],
  WebControlPanelSteps[WebStep.tradeIn],
  WebControlPanelSteps[WebStep.appointments],
  WebControlPanelSteps[WebStep.accessories],
  WebControlPanelSteps[WebStep.protectionPlans],
];

export const getDisabledOnlineSteps = (dealer: Dealer): OnlineStep[] => [
  ...(get('featureConfig.deposits.enabled')(dealer)
    ? []
    : [OnlineStep.deposit]),
  ...(get('featureConfig.eContracting.enabled')(dealer)
    ? []
    : [OnlineStep.signDocuments]),
];

const getOnlineFlow = (dealer: Dealer) => {
  const disabledSteps = getDisabledOnlineSteps(dealer);
  const configuredOnlineSteps = get('stepFlowConfig.online')(dealer);
  const onlineSteps =
    configuredOnlineSteps && configuredOnlineSteps.length > 0
      ? configuredOnlineSteps
      : getDefaultOnlineFlow();
  return onlineSteps.filter(
    (step: { id: OnlineStep }) => !includes(step.id, disabledSteps),
  );
};

export const getDealerFlow = (dealer: Dealer) => {
  const instoreSteps = get('stepFlowConfig.instore')(dealer);
  return {
    [CLIENT_TYPE.IN_STORE]:
      instoreSteps && instoreSteps.length > 0
        ? dealer.stepFlowConfig.instore
        : getDefaultInstoreFlow(),
    [CLIENT_TYPE.ONLINE]: getOnlineFlow(dealer),
  };
};

export const getDependsForStep = (dealer: Dealer, id: string) => {
  const flow = getDealerFlow(dealer);
  const step = find<any>({ id })(flow[CLIENT_TYPE.IN_STORE]);
  return getOr<string[]>([], 'depends')(step);
};

export default { getDealerFlow };
